// Hook pour lazy loading avec Intersection Observer
import { useEffect, useRef, useState } from 'react';
import { AdaptivePreloadService } from '../Services/AdaptivePreloadService';
import { useScrollSpeed } from './useScrollSpeed';
import { useNetworkSpeed } from './useNetworkSpeed';
import { LazyLoadLogger } from '../Services/LazyLoadLogger';

export interface UseLazyComponentOptions {
    rootMargin?: string;         // Rootmargin fixe (si défini, ignore la distance adaptative)
    threshold?: number;
    enabled?: boolean;
    useAdaptiveDistance?: boolean; // Utiliser la distance adaptative (défaut: true)
    forceLoadAll?: boolean;      // Forcer le chargement de toute la page (défaut: true)
    sequentialDelay?: number;    // Délai entre chargements séquentiels (défaut: 100ms)
}

export interface UseLazyComponentReturn {
    isVisible: boolean;
    ref: React.RefObject<HTMLDivElement | null>;
    preloadDistance?: number;     // Distance de préchargement calculée (si adaptative)
}

/**
 * Hook pour détecter quand un composant entre dans le viewport
 * Utilise Intersection Observer avec distance adaptative optionnelle
 */
export function useLazyComponent(
    options: UseLazyComponentOptions = {}
): UseLazyComponentReturn {
    const {
        rootMargin: fixedRootMargin,
        threshold = 0.01,
        enabled = true,
        useAdaptiveDistance = true,
        forceLoadAll = true,        // Charger toute la page par défaut
        sequentialDelay = 100        // 100ms entre chaque composant
    } = options;

    const [isVisible, setIsVisible] = useState(!enabled);
    const [preloadDistance, setPreloadDistance] = useState<number>();
    const ref = useRef<HTMLDivElement>(null);
    
    const { scrollSpeed, lastActiveSpeed } = useScrollSpeed();
    const { speed: networkSpeed } = useNetworkSpeed();

    useEffect(() => {
        // Si désactivé, toujours visible
        if (!enabled) {
            setIsVisible(true);
            return;
        }

        const element = ref.current;
        if (!element) return;

        // Vérifier le support d'Intersection Observer
        if (!('IntersectionObserver' in window)) {
            console.warn('IntersectionObserver non supporté, lazy loading désactivé');
            setIsVisible(true);
            return;
        }

        // Calculer le rootMargin
        let calculatedRootMargin: string;
        
        if (fixedRootMargin) {
            // Utiliser la valeur fixe si fournie
            calculatedRootMargin = fixedRootMargin;
        } else if (useAdaptiveDistance) {
            // Calculer la distance adaptative avec lastActiveSpeed
            const distance = AdaptivePreloadService.calculatePreloadDistance(
                scrollSpeed,
                networkSpeed,
                lastActiveSpeed
            );
            
            // Si la distance calculée est trop faible, utiliser un minimum généreux
            // Cela arrive au premier chargement avant le premier scroll
            const minInitialDistance = 1200; // Distance minimale initiale généreuse (augmentée à 1200px)
            const finalDistance = Math.max(distance, minInitialDistance);
            
            calculatedRootMargin = AdaptivePreloadService.getAsRootMargin(finalDistance);
            setPreloadDistance(finalDistance);
            
            LazyLoadLogger.logViewport(`Distance de préchargement: ${finalDistance}px`, {
                scrollSpeed: scrollSpeed.toFixed(0),
                lastActiveSpeed: lastActiveSpeed.toFixed(0),
                networkSpeed,
                calculated: distance,
                applied: finalDistance,
                boosted: finalDistance > distance
            });
        } else {
            // Par défaut, utiliser 200px
            calculatedRootMargin = '200px';
        }

        LazyLoadLogger.logViewport(`🔧 Création IntersectionObserver`, {
            rootMargin: calculatedRootMargin,
            threshold,
            element: element.id || element.className
        });

        // Vérification manuelle AVANT d'observer (pour détecter éléments déjà dans zone)
        const checkInitialVisibility = () => {
            const rect = element.getBoundingClientRect();
            const viewportHeight = window.innerHeight;
            
            // Calculer les bounds étendus selon le rootMargin
            const distance = useAdaptiveDistance ? (preloadDistance || 1200) : 200;
            const extendedTop = -distance;
            const extendedBottom = viewportHeight + distance;
            
            // Vérifier si l'élément est dans la zone étendue
            const isInExtendedViewport = rect.bottom >= extendedTop && rect.top <= extendedBottom;
            
            // Vérifier si dans viewport actuel
            const isInViewport = rect.bottom >= 0 && rect.top <= viewportHeight;
            
            LazyLoadLogger.logViewport('🔍 Vérification initiale', {
                elementTop: rect.top.toFixed(0),
                elementBottom: rect.bottom.toFixed(0),
                extendedTop,
                extendedBottom,
                isInExtendedViewport,
                isInViewport,
                zone: isInViewport ? 'VIEWPORT' : isInExtendedViewport ? 'PRELOAD (deadline 3s)' : 'HORS ZONE'
            });
            
            if (isInExtendedViewport) {
                setIsVisible(true);
                
                if (!isInViewport) {
                    // Composant dans zone de préchargement → DEADLINE 3s
                    LazyLoadLogger.warn('viewport', `⏰ Composant en zone PRELOAD - DEADLINE 3s`, {
                        top: rect.top.toFixed(0),
                        deadline: '3000ms'
                    });
                    
                    // Vérifier après 3s si bien chargé
                } else {
                    LazyLoadLogger.logViewport('✅ Composant dans viewport (immédiat)');
                }
                
                return true;
            }
            
            return false;
        };
        
        // Vérifier d'abord manuellement
        const alreadyVisible = checkInitialVisibility();
        
        // Si déjà visible, pas besoin d'observer
        if (alreadyVisible) {
            LazyLoadLogger.logViewport('⏭️ Observer non nécessaire (déjà visible/préchargé)');
            return;
        }

        // Si forceLoadAll = true, charger même les composants hors zone après un délai
        if (forceLoadAll && !alreadyVisible) {
            const rect = element.getBoundingClientRect();
            const viewportHeight = window.innerHeight;
            
            // Calculer le délai selon la distance (plus loin = plus tard)
            // Composants dans 2000px : 3s
            // Composants dans 4000px : 5s
            // Composants dans 6000px : 7s
            const distanceFromExtended = Math.max(0, rect.top - (viewportHeight + 1200));
            const delay = 3000 + (distanceFromExtended / 1000) * sequentialDelay;
            
            LazyLoadLogger.logData(`📅 Chargement programmé dans ${(delay / 1000).toFixed(1)}s`, {
                elementTop: rect.top.toFixed(0),
                distanceFromExtended: distanceFromExtended.toFixed(0),
                delay: `${(delay / 1000).toFixed(1)}s`
            });
            
            // Programmer le chargement
            const loadTimeout = setTimeout(() => {
                LazyLoadLogger.logData(`⏰ Chargement séquentiel déclenché`);
                setIsVisible(true);
            }, delay);
            
            // Nettoyer le timeout au démontage
            return () => {
                clearTimeout(loadTimeout);
            };
        }

        // Sinon, créer l'observer pour les changements futurs (si pas forceLoadAll)
        const observer = new IntersectionObserver(
            ([entry]) => {
                LazyLoadLogger.logViewport(`📍 IntersectionObserver callback`, {
                    isIntersecting: entry.isIntersecting,
                    intersectionRatio: entry.intersectionRatio,
                    boundingClientRect: {
                        top: entry.boundingClientRect.top,
                        bottom: entry.boundingClientRect.bottom
                    },
                    rootBounds: entry.rootBounds ? {
                        top: entry.rootBounds.top,
                        bottom: entry.rootBounds.bottom
                    } : null
                });

                if (entry.isIntersecting) {
                    setIsVisible(true);
                    LazyLoadLogger.logViewport('✅ Composant visible/préchargé (observer)', {
                        intersectionRatio: entry.intersectionRatio
                    });
                    // Une fois visible, arrêter d'observer
                    observer.disconnect();
                }
            },
            {
                rootMargin: calculatedRootMargin,
                threshold
            }
        );

        observer.observe(element);
        LazyLoadLogger.logViewport('👀 Observer activé pour changements futurs');

        return () => {
            LazyLoadLogger.logViewport('🛑 Observer déconnecté');
            observer.disconnect();
        };
    }, [fixedRootMargin, threshold, enabled, useAdaptiveDistance]);

    return { isVisible, ref, preloadDistance };
}

